import React, {useState} from 'react';
import PropTypes from 'prop-types';
import styles from '../SearchWidget.module.scss';
import {connect} from "react-redux";
import submitBtnImgPath from "../../../assets/media/submitIcon.svg";
import WindowEventWatcher from "../../Common/WindowEventWatcher";
import Field from "../../../tools/Field";
import {withTranslation} from "react-i18next";
import Popup from "../Common/Popup";
import Checkbox from "../../Common/Checkbox/Checkbox";
import {replaceSpecialChars} from "../../../tools/tools";
import axios from "axios";
import {APP_CONFIGS} from '../../../config/configs';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSpinner} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../Common/Modal/Modal";


const CheckInForm = ({lang, formRef, t}) => {

    const [error, setError] = useState('');
    const [checkBoxChecked, setCheckBoxChecked] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [popupOpen, setPopupOpen] = useState(false);
    const [formAction, setFormAction] = useState('');
    const [fields, setFields] = useState({
        bookingCode: new Field('bookingCode', '', '', v => /^[A-Za-z0-9]{0,6}$/g.test(v), v => /^[A-Za-z0-9]{6}$/g.test(v)),
        lastName: new Field('lastName', '', '', v => /^.*$/g.test(v), v => /^.+$/g.test(v)),
    });

    const mapLang = {en: 'en_US', pt: 'pt_PT'};

    const retrieveAmadeusLinkAndSubmit = () => {
        setSubmitting(true);
        const url = `${APP_CONFIGS.API_ENDPOINT}/webcheckinurl/${fields.bookingCode.getValue()}/${fields.lastName.getValue()}/` + mapLang[lang];
        axios.get(url)
            .then(response => {
                if (response.data.url) {
                    setFormAction(response.data.url);
                    formRef.current.submit();
                } else {
                    setError(t(response.data.errorMessage));
                }
            })
            .catch(error => {
                console.log(error.message);
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const handleSubmit = e => {
        const validation = Field.validateAllFields(fields);
        setFields({...validation.fields});
        if (validation.isAllValid && checkBoxChecked) {
            retrieveAmadeusLinkAndSubmit();
        }
    };

    const onChange = (field, e) => {
        const value = replaceSpecialChars(e.target.value);

        if (field.validate(value, true).getIsInvalid()) {
            setTimeout(() => {
                field.validate();
                setFields({...fields, [field.key]: {...field}});
            }, 1000);
        } else {
            field.setValue(value);
        }

        setFields({...fields, [field.key]: {...field}});
    };

    const onBlurHandler = (field, e) => {
        field.validate();
        setFields({...fields, [field.key]: {...field}});
    };

    const keyDownHandler = e => {
        const code = e.code ? e.code : e.key;

        switch (code) {
            case 'NumpadEnter':
            case 'Enter':
                handleSubmit();
                break;
            default:
                return;
        }

        e.preventDefault();
        e.stopPropagation();
    };

    const isDisabled = !(fields.bookingCode.getIsValid() && fields.lastName.getIsValid()) || !checkBoxChecked;
    const submitClasses = [styles.checkInFormSubmitBtnContainer];
    if (isDisabled) submitClasses.push(styles.checkInFormSubmitBtnDisabled);

    return (
        <div className={styles.checkInFormExternalContainer}>
            <div className={styles.checkInFormContainer}>
                <WindowEventWatcher handlerFn={keyDownHandler} eventName={'keydown'}/>
                <form method='POST' action={formAction} ref={formRef}>
                    <input type='hidden' name='form_id' value='check-in-form'/>
                    <input type='hidden' name='form_build_id' value={window.checkInFormId || ''}/>
                    <input type='hidden' name='confirm' value={checkBoxChecked ? '1' : '0'}/>
                    <div className={styles.checkInFormInputContainer}>
                        <label htmlFor={'booking-code-field'}>
                            {t('Booking code')}
                        </label>
                        <div className={styles.checkInFormInputContainerInner}>
                            <input
                                autoCorrect='none'
                                autoCapitalize='none'
                                spellCheck='none'
                                id={'booking-code-field'}
                                name='booking-code'
                                size={15}
                                maxLength={60}
                                type='text'
                                placeholder={t('e.g. X2C1V3')}
                                className={fields.bookingCode.getIsInvalid() ? styles.genericFormInvalidInput : ''}
                                value={fields.bookingCode.getValue()}
                                onChange={e => onChange(fields.bookingCode, e)}
                                onBlur={e => onBlurHandler(fields.bookingCode, e)}
                                required
                                aria-required/>
                            <i className={[styles.genericInfoIcon, 'icon-sata-info-icon'].join(' ')}
                               title={t('What is a booking code?')}
                               onClick={() => setPopupOpen(true)}>
                                <Popup show={popupOpen} closeHandler={e => {
                                    e.stopPropagation();
                                    setPopupOpen(false);
                                }}>
                                        <span style={{
                                            fontFamily: 'NeoSansMedium',
                                            width: '300px',
                                            fontSize: '0.85em'
                                        }}>{t('What is a booking code?')}</span>
                                    <span
                                        style={{fontSize: '0.85em'}}>{t('It is an alphanumeric code with 6 characters')}.</span>
                                    <span
                                        style={{fontSize: '0.85em'}}>{t("You can find it in your electronic ticket in 'Booking reference'")}.</span>
                                </Popup>
                            </i>
                        </div>
                    </div>
                    <div className={styles.checkInFormInputContainer} style={{position: 'relative'}}>
                        <Modal open={!!error} closeBtnVisible={false} backdropHandler={() => setError('')}>
                            <Popup show={!!error} closeHandler={() => setError('')} styleOverride={{position: 'sticky', minWidth: '100%'}}>
                                <span>{error}</span>
                            </Popup>
                        </Modal>
                        <label htmlFor={'last-name-field'}>
                            {t('Last name')}
                        </label>
                        <div className={styles.checkInFormInputContainerInner}>
                            <input
                                autoCorrect='none'
                                autoCapitalize='none'
                                autoComplete='off'
                                spellCheck='none'
                                id={'last-name-field'}
                                name='last-name'
                                size={60}
                                maxLength={128}
                                className={fields.lastName.getIsInvalid() ? styles.genericFormInvalidInput : ''}
                                value={fields.lastName.getValue()}
                                onChange={e => onChange(fields.lastName, e)}
                                onBlur={e => onBlurHandler(fields.lastName, e)}
                                type='text'
                                placeholder={t('Last name')}
                                required
                                aria-required/>
                            &nbsp;
                        </div>
                    </div>
                    <div className={styles.checkInFormConditionsContainer}
                         onMouseDown={() => setCheckBoxChecked(!checkBoxChecked)}>
                        <Checkbox clickHandler={() => setCheckBoxChecked(!checkBoxChecked)}
                                  checked={checkBoxChecked}/>&nbsp;
                        <span>
                            {t("I've read and accept the")}
                            &nbsp;<a href={t('/en/before-boarding/check-in')}
                                     target={'_blank'}>{t('conditions')}</a>&nbsp;
                            {t('and took notice of the')}
                            &nbsp;<a href={t('/en/information/prohibited-articles')}
                                     target={'_blank'}>{t('prohibited articles')}</a>&nbsp;
                            {t('to transport')}.
                        </span>
                    </div>
                    <div onClick={handleSubmit}
                         className={submitClasses.join(' ')}
                         tabIndex={0}>
                        {submitting ? <FontAwesomeIcon icon={faSpinner} spin/> :
                            <img src={submitBtnImgPath} alt={'manage trip submit btn'}/>}
                    </div>
                </form>
            </div>
            <div className={styles.checkInFormBottomArea} id={'search-widget-book-flight-opts'}/>
        </div>
    );
};

CheckInForm.propTypes = {
    formRef: PropTypes.any,
    lang: PropTypes.string,
    t: PropTypes.func,
};

CheckInForm.defaultProps = {
    formRef: React.createRef(),
    lang: 'en',
    t: s => s,
};

export default withTranslation()(connect(state => ({lang: state.common.lang}))(CheckInForm));

